import { useCallback, useState, useEffect, useMemo, lazy, Suspense } from "react";
import { hasCredentials } from "./api/client";
// EditorView est chargé sync (= la page d'éditeur est le cas d'usage principal,
// pas la peine de retarder son apparition pour économiser quelques KB).
import EditorView from "./components/EditorView";
import ConnectionToast from "./components/ConnectionToast";
import { useLabelStore } from "./store/useLabelStore";
import { useTabsStore } from "./store/useTabsStore";
import { useAuthStore } from "./store/useAuthStore";

// Les pages d'auth sont lazy-loadées : un utilisateur déjà connecté (la majorité
// des sessions) n'a jamais besoin de leur code → autant de KB en moins au load
// initial du dashboard. Suspense fallback minimal (rien d'animé, transition
// quasi-instantanée vu que le chunk est petit).
const LoginPage = lazy(() => import("./components/auth/LoginPage"));
const RegisterPage = lazy(() => import("./components/auth/RegisterPage"));
const VerifyEmailPage = lazy(() => import("./components/auth/VerifyEmailPage"));
const ForgotPasswordPage = lazy(() => import("./components/auth/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./components/auth/ResetPasswordPage"));
// Page publique read-only ouverte via /preview/<token> — pas d'auth requise,
// chargée à la demande pour ne pas alourdir le bundle des utilisateurs réguliers.
const PreviewPage = lazy(() => import("./components/PreviewPage"));
// Route /print : rendu propre du canvas pour l'export PDF via headless Chrome.
const PrintView = lazy(() => import("./components/PrintView"));
// Route /rapports : la bibliothèque des comptes rendus. Rendue APRÈS le contrôle
// d'authentification — un visiteur non connecté tombe sur la page de connexion.
const RapportsPage = lazy(() => import("./components/RapportsPage"));
// Route /studio-ia-beta : le Studio I.A Beta (workstation RTX 5090), ouvert dans un
// nouvel onglet depuis le menu Fichier. Même régime que /rapports : après la connexion.
const StudioIABetaPage = lazy(() => import("./studio-ia/StudioIABetaPage"));
// Route /pilotage : le pilotage des agents (consignes versionnées), ouvert dans un nouvel
// onglet depuis l'onglet Agent du Studio IA. Même régime : après la connexion, admin seulement.
const PilotagePage = lazy(() => import("./pilotage/PilotagePage"));
// Banc d'essai DEV de la boîte de dialogue d'export (route /export-test, dev only).
const ExportDevHarness = lazy(() => import("./components/ExportDevHarness"));

// Fallback minimaliste pour Suspense : un écran noir le temps de fetcher le chunk
// (typiquement < 500ms sur connexion correcte, instantané sur 4G+ et cache navigateur).
function AuthLoadingFallback() {
  return (
    <div className="h-screen w-full bg-black flex items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-2 border-white/15 border-t-white/60 rounded-full animate-spin" />
        <span className="text-[10px] font-mono uppercase tracking-widest text-white/30">Chargement...</span>
      </div>
    </div>
  );
}

export default function App() {
  const [loggedIn, setLoggedIn] = useState(false);
  const [authPage, setAuthPage] = useState('login'); // 'login', 'register', 'forgot', 'verify-email'
  const { isAuthenticated } = useAuthStore();

  // Detect special auth links in URL.
  //   /verify-email?token=XXX     → email verification flow
  //   /reset-password?token=XXX   → password reset flow
  // Avant : tout `?token=` etait traite comme verify-email, ce qui cassait le reset password.
  const authLinkRoute = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const path = window.location.pathname;
    const token = params.get('token');
    // Route /print : rendu propre pour l'export PDF headless Chrome.
    if (path === '/print' || path === '/print/') return { kind: 'print' };
    // Route DEV /export-test : banc d'essai du dialogue d'export (jamais en prod).
    if (import.meta.env.DEV && (path === '/export-test' || path === '/export-test/')) return { kind: 'export-test' };
    // Bibliothèque des rapports : /rapports — protégée, cf. plus bas.
    if (path === '/rapports' || path === '/rapports/') return { kind: 'rapports' };
    // Studio I.A Beta : /studio-ia-beta — protégé, réservé aux admins (cf. plus bas).
    if (path === '/studio-ia-beta' || path === '/studio-ia-beta/') return { kind: 'studio-ia' };
    // Pilotage des agents : /pilotage — protégé, réservé aux admins (cf. plus bas).
    if (path === '/pilotage' || path.startsWith('/pilotage/')) return { kind: 'pilotage' };
    // Public read-only share : /preview/<token>
    const previewMatch = path.match(/^\/preview\/([A-Za-z0-9_-]+)\/?$/);
    if (previewMatch) return { kind: 'preview', token: previewMatch[1] };
    if (path.includes('reset-password')) return { kind: 'reset', token };
    if (path.includes('verify-email')) return { kind: 'verify', token };
    return null;
  }, []);

  const handleLogin = useCallback(() => {
    setLoggedIn(true);
  }, []);

  // Note: Undo/Redo keyboard shortcuts are handled in LabelCanvas.jsx (avoid double-fire)

  // Initialize tabs system + history on load
  useEffect(() => {
    if (loggedIn || hasCredentials()) {
      // ── ATTENDRE LA RÉHYDRATATION AVANT DE TOUCHER AUX ONGLETS ──
      //
      // Le document vit dans IndexedDB et se recharge de façon ASYNCHRONE.
      // Ouvrir les onglets avant, c'était ouvrir un onglet VIDE, le charger
      // dans le document, et poser le tout premier point d'annulation sur ce
      // vide. Le document réel arrivait ensuite ; l'historique, lui, n'est pas
      // persisté, donc il gardait son instantané vide. Un seul Ctrl+Z après un
      // F5 rendait alors l'étiquette entière au néant, format compris.
      //
      // (Auparavant, `init()` retrouvait le projet dans localStorage. La clé y
      // a disparu le jour du passage à IndexedDB, qui la supprime après l'avoir
      // migrée : la branche de restauration était morte depuis, sans un bruit.)
      const demarrer = () => {
        useTabsStore.getState().init();
        const store = useLabelStore.getState();
        if (store.history && store.history.length === 0) {
          store._pushState('Ouverture du projet');
        }
      };
      if (useLabelStore.persist?.hasHydrated?.()) demarrer();
      else if (useLabelStore.persist?.onFinishHydration) {
        useLabelStore.persist.onFinishHydration(demarrer);
      } else {
        demarrer();  // pas de persistance (test, banc) → rien à attendre
      }

      // Re-injecte les <link> Google Fonts persistés (l'utilisateur a chargé
      // des fonts custom dans une session précédente — sans rehydrate, leurs
      // noms sont dans la liste mais les CSS ne sont pas chargés → les
      // calques qui les utilisent retombent sur la font par défaut).
      import('./store/useFontStore').then(({ useFontStore }) => {
        useFontStore.getState().rehydrateGoogleFonts();
        // Recharge les polices IMPORTÉES (.ttf/.otf) depuis IndexedDB et les
        // ré-enregistre dans document.fonts → les calques qui les utilisent
        // s'affichent correctement après un refresh (sinon font par défaut).
        useFontStore.getState().rehydrateUploadedFonts();
      });
    }
  }, [loggedIn]);

  // Warn before closing if unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (useTabsStore.getState().hasUnsavedChanges()) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Intercept special public/auth links
  if (authLinkRoute) {
    // Page publique de prévisualisation (read-only, pas d'auth requise) —
    // priorité absolue : un utilisateur peut être authentifié ET ouvrir
    // un /preview/... dans le même navigateur.
    if (authLinkRoute.kind === 'preview') {
      return (
        <Suspense fallback={<AuthLoadingFallback />}>
          <PreviewPage token={authLinkRoute.token} />
        </Suspense>
      );
    }
    // Route /print : rendu propre du canvas pour l'export PDF (headless Chrome).
    // Pas d'auth (le service Playwright tourne en interne) ; projet injecté via
    // window.__LF_PROJECT__ ou ?data=base64.
    if (authLinkRoute.kind === 'print') {
      return (
        <Suspense fallback={null}>
          <PrintView />
        </Suspense>
      );
    }
    if (authLinkRoute.kind === 'export-test') {
      return (
        <Suspense fallback={null}>
          <ExportDevHarness />
        </Suspense>
      );
    }
    const goToLogin = () => {
      // Strip path + query params and reload on the login page
      window.history.replaceState({}, '', '/');
      window.location.reload();
    };
    if (authLinkRoute.kind === 'reset') {
      return (
        <Suspense fallback={<AuthLoadingFallback />}>
          <ResetPasswordPage token={authLinkRoute.token} onGoToLogin={goToLogin} />
        </Suspense>
      );
    }
    if (authLinkRoute.kind === 'verify') {
      return (
        <Suspense fallback={<AuthLoadingFallback />}>
          <VerifyEmailPage token={authLinkRoute.token} onGoToLogin={goToLogin} />
        </Suspense>
      );
    }
  }

  // Show auth pages if not authenticated (JWT or legacy Basic Auth)
  if (!isAuthenticated && !loggedIn && !hasCredentials()) {
    return (
      <Suspense fallback={<AuthLoadingFallback />}>
        {authPage === 'register' ? (
          <RegisterPage onSwitchToLogin={() => setAuthPage('login')} />
        ) : authPage === 'forgot' ? (
          <ForgotPasswordPage onSwitchToLogin={() => setAuthPage('login')} />
        ) : (
          <LoginPage
            onSwitchToRegister={() => setAuthPage('register')}
            onSwitchToForgot={() => setAuthPage('forgot')}
          />
        )}
      </Suspense>
    );
  }

  // Ici, et pas plus haut : on ne sert la bibliothèque qu'à une session ouverte.
  if (authLinkRoute?.kind === 'rapports') {
    return (
      <Suspense fallback={<AuthLoadingFallback />}>
        <RapportsPage />
      </Suspense>
    );
  }
  if (authLinkRoute?.kind === 'studio-ia') {
    return (
      <Suspense fallback={<AuthLoadingFallback />}>
        <StudioIABetaPage />
      </Suspense>
    );
  }
  if (authLinkRoute?.kind === 'pilotage') {
    return (
      <Suspense fallback={<AuthLoadingFallback />}>
        <PilotagePage />
      </Suspense>
    );
  }

  return (
    <div className="h-screen w-full bg-black/20 text-white flex overflow-hidden">
      <EditorView />
      {/* Toast persistant en cas de coupure réseau ou backend down. Reste
          monté en permanence pour réagir aux events de tous les onglets. */}
      <ConnectionToast />
    </div>
  );
}
